/*
 * Copyright (c) 2019. World Wide Generation Ltd
 */

import SearchComponent, { SearchComponentProps } from './SearchComponent';
import { searchUserByOrganization, searchUserByInitiative, searchUserBySurvey } from '../../actions/common';
import { Avatar } from '@g17eco/atoms/avatar';
import { searchUserByAssurancePortfolio } from '../../actions/assurance';
import { formatData, formatWorkgroupData, renderWorkgroupSuggestion } from './utils';
import { isOnboardingUser } from '@utils/user';
import { Suggestion } from '@g17eco/types/search';
import { Workgroup } from '@g17eco/types/workgroup';

const defaultRenderSuggestion = (suggestion: Suggestion) => {
  return suggestion.isWorkgroup ? renderWorkgroupSuggestion(suggestion) : (
    <div className='suggestion-item'>
      <Avatar width='30px'>{suggestion.profile ? <img alt='avatar' src={suggestion.profile} /> : <></>}</Avatar>
      <div className='name'>
        <div>
          <strong>{suggestion.name}</strong>
        </div>
        <div>
          <small>{suggestion.jobTitle}</small>
        </div>
        <div className='dont_translate'>
          <small>{suggestion.email}</small>
        </div>
      </div>
    </div>
  );
}

interface UserSearchProps extends Omit<SearchComponentProps, 'renderSuggestion'> {
  organizationId?: string;
  surveyId?: string;
  initiativeId?: string;
  assurancePortfolioId?: string;
  ignoredIds?: string[];
  hideOnboardings?: boolean;
  renderSuggestion?: (suggestion: Suggestion) => JSX.Element;
}

const UserSearch = (props: UserSearchProps) => {
  const { hideOnboardings = false, ignoredIds, renderSuggestion = defaultRenderSuggestion } = props;
  let searchAction: undefined | ((searchStr: string) => Promise<Suggestion[]>);

  const filterIgnoredIds = (suggestions: Suggestion[]) => {
    if (ignoredIds && Array.isArray(suggestions)) {
      return suggestions.filter((s) => {
        if (ignoredIds.includes(s._id)) {
          return false;
        }
        if (hideOnboardings) {
          return !isOnboardingUser(s.status);
        }
        return true;
      });
    }
    return suggestions ?? [];
  }

  switch (true) {
    case !!props.organizationId: {
      const { organizationId } = props;
      searchAction = s => searchUserByOrganization(s, organizationId).then(filterIgnoredIds);
      break;
    }
    case !!props.surveyId: {
      const { surveyId } = props;
      searchAction = (s) =>
        searchUserBySurvey(s, surveyId).then(
          (res: { users: Suggestion[]; workgroups: Workgroup[] } | Suggestion[]) => {
            if ('workgroups' in res) {
              const users = filterIgnoredIds(formatData(res.users));
              const workgroups = filterIgnoredIds(formatWorkgroupData(res.workgroups));
              return [...users, ...workgroups];
            }
            return filterIgnoredIds(formatData(res)); // Backwards compatibility
          },
        );
      break;
    }
    case !!props.initiativeId: {
      const { initiativeId } = props;
      searchAction = s => searchUserByInitiative(s, initiativeId).then(filterIgnoredIds);
      break;
    }
    case !!props.assurancePortfolioId: {
      const { assurancePortfolioId } = props;
      searchAction = s => searchUserByAssurancePortfolio(s, assurancePortfolioId).then(filterIgnoredIds);
      break;
    }
    default:
  }

  const placeholder = props.placeholder || 'Search user';
  if (!searchAction) {
    return <input className='form-control' disabled={true} placeholder={placeholder} />;
  }

  return (
    <SearchComponent
      searchAction={searchAction}
      autoComplete={'user'}
      {...props}
      renderSuggestion={renderSuggestion}
      placeholder={placeholder}
    />
  );
}

export default UserSearch;
